import { Link } from "react-router-dom";
import Mail from "lucide-react/dist/esm/icons/mail.js";
import MapPin from "lucide-react/dist/esm/icons/map-pin.js";
import Phone from "lucide-react/dist/esm/icons/phone.js";
import { useI18n, localized, pick } from "@/lib/i18n";
import { COPY } from "@/lib/uiCopy";
import { useQuery } from "@tanstack/react-query";
import { fetchServices, fetchSettings } from "@/lib/queries";
import { fallbackServices, mergeServiceFallbacks, servicePath } from "@/lib/serviceData";
import { useLp } from "@/lib/localePath";
import { useDeferredReady } from "@/hooks/use-deferred-ready";
import logoWhiteUrl from "@/assets/logo-white.webp";

export function Footer() {
  const { t, locale } = useI18n();
  const lp = useLp();
  const ready = useDeferredReady(1500);
  const { data: services } = useQuery({ queryKey: ["services"], queryFn: fetchServices, enabled: ready });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: fetchSettings, enabled: ready });

  const get = (k: string) => {
    const s = settings?.get(k);
    return s ? (s[`value_${locale}`] || s.value_tr || s.value_en) || "" : "";
  };

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container-page py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="mb-4">
            <Link to={lp("/")} aria-label="Anadolu Terra ana sayfa" className="inline-block">
              <img
                src={logoWhiteUrl}
                alt="Anadolu Terra"
                width={140}
                height={48}
                loading="lazy"
                decoding="async"
                className="h-12 w-auto object-contain"
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </Link>
          </div>
          <p className="text-sm text-primary-foreground/70 leading-relaxed">{pick(locale, COPY.slogan)}</p>
        </div>

        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-wider mb-4 text-primary-foreground">{t("footer.quickLinks")}</h3>
          <ul className="space-y-2 text-sm">
            <li><Link to={lp("/felsefemiz")} className="text-primary-foreground/80 hover:text-primary-foreground transition-base">{t("nav.philosophy")}</Link></li>
            <li><Link to={lp("/hizmetlerimiz")} className="text-primary-foreground/80 hover:text-primary-foreground transition-base">{t("nav.services")}</Link></li>
            <li><Link to={lp("/hizmet-alanlarimiz")} className="text-primary-foreground/80 hover:text-primary-foreground transition-base">{t("nav.serviceAreas")}</Link></li>
            <li><Link to={lp("/insan-kaynaklari")} className="text-primary-foreground/80 hover:text-primary-foreground transition-base">{t("nav.hr")}</Link></li>
            <li><Link to={lp("/teklif-iste")} className="text-primary-foreground/80 hover:text-primary-foreground transition-base">{t("nav.quote")}</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-wider mb-4 text-primary-foreground">{t("footer.services")}</h3>
          <ul className="space-y-2 text-sm">
            {mergeServiceFallbacks(services && services.length > 0 ? services : fallbackServices).slice(0, 6).map((s) => (
              <li key={s.id}>
                <Link to={lp(servicePath(s.slug))} className="text-primary-foreground/80 hover:text-primary-foreground transition-base">
                  {localized(s, "title", locale)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-wider mb-4 text-primary-foreground">{t("footer.contact")}</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2.5">
              <Phone className="h-4 w-4 mt-0.5 shrink-0 text-primary-foreground" />
              <a href="tel:02162221144" className="text-primary-foreground/85 hover:text-primary-foreground">0216 222 11 44</a>
            </li>
            <li className="flex items-start gap-2.5">
              <Mail className="h-4 w-4 mt-0.5 shrink-0 text-primary-foreground" />
              <a href="mailto:info@anadoluterra.com" className="text-primary-foreground/85 hover:text-primary-foreground">info@anadoluterra.com</a>
            </li>
            <li className="flex items-start gap-2.5">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-primary-foreground" />
              {/* NAP tutarlılığı: bu metin schema.org PostalAddress, llms.txt, llm.txt ve
                  /iletisim JSON-LD'siyle BİREBİR aynı kalmalı. Footer daha önce farklı bir
                  adres gösteriyordu (Ayanoğlu İş Merkezi, Gazi Mustafa Kemal Cd. No:1
                  Daire:1) — aynı sayfada iki farklı adres, yerel SEO'da doğrudan zayıf
                  sinyal. Değiştirirken diğer dört yeri de güncelle. */}
              <span className="text-primary-foreground/85">Atatürk Mahallesi Mustafa Kemal Caddesi No:1 Kat:1, Ataşehir/İstanbul</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-primary-foreground/10">
        <div className="container-page py-5 text-xs text-primary-foreground/60 flex flex-col md:flex-row items-center justify-between gap-2">
          <p>© {new Date().getFullYear()} Anadolu Terra A.Ş. {t("footer.rights")}</p>
          <p>{pick(locale, COPY.footerSubtitle)}</p>
          <a
            href="https://yapayzekahizmetleri.com.tr"
            target="_blank"
            rel="noopener"
            className="font-semibold text-primary-foreground hover:text-primary-foreground/80 transition-base"
          >
            YZH — AI Transformation Partner
          </a>
        </div>
      </div>
    </footer>
  );
}
